<template>
  <v-dialog
    transition="scale-transition"
    origin="center center"
    id="two_factor_action"
    :model-value="show"
    :max-width="554"
    :fullscreen="$vuetify.display.xs"
    persistent
  >
    <slot v-if="currentStep === 0" :start="requestOTP" />
    <v-card v-else id="otp-card" class="text-center">
      <v-container class="pa-0">
        <AlertsComponent :context="alertContext" />
      </v-container>
      <v-container>
        <v-row justify="center">
          <v-col xs="10" sm="10" md="10">
            <h2 class="heading_two mt-4 mb-2">
              {{ $t('L_verify_your_mobile_phone_number') }}
            </h2>
          </v-col>
        </v-row>
        <v-row class="pl-0 pr-0">
          <form @submit.prevent ref="twoFactorForm">
            <v-col class="text-left mb-4 mt-0 px-6">
              <i18n-t
                keypath="L_two_step_authentication_helps_keep_your_account_more_secure"
                tag="p"
                class="text-center"
              >
                <template #contactNumber>
                  <span>{{
                    twoFactorPayload.channel === 'SMS'
                      ? formattedContactNumber(twoFactorPayload.phone)
                      : twoFactorPayload.email
                  }}</span>
                </template>
                <template #textOrEmail>
                  {{
                    twoFactorPayload.channel === 'SMS' ? $t('L_a_text_message') : $t('L_an_email')
                  }}
                </template>
                <template #ifTheMobileNumberIsIncorrect>
                  <!-- v-if -->
                  <span v-if="$route.name === RouteNames.MY_ACCOUNT">{{
                    $t('L_if_the_mobile_number_above_is_incorrect_you_may')
                  }}</span>
                  <!-- v-else -->
                  <span
                    v-if="
                      $route.name === RouteNames.DASHBOARD ||
                      $route.name === RouteNames.ALERT_AND_NOTIFICATIONS
                    "
                  >
                    {{ $t('L_if_the_mobile_number_above_is_incorrect_you_can_change_it') }}</span
                  >
                </template>
                <template #editItHere>
                  <span
                    v-if="$route.name === RouteNames.MY_ACCOUNT"
                    class="editItHere"
                    @click="goBackToEdit"
                    >{{ $t('L_edit_it_here') }}</span
                  >
                </template>
              </i18n-t>
            </v-col>
            <v-col class="mb-4 pt-2 pb-0 px-6">
              <span class="font-weight-bold font-size--14">{{
                $t('L_enter_the_verification_code')
              }}</span>
            </v-col>
            <v-row class="font-weight-regular ml-2 mr-2">
              <v-col cols="12" class="mb-4 mt-2 px-6">
                <OnexPasscode
                  v-model="otpCode"
                  id="opt-code"
                  :label="$t('L_enter_code')"
                  required
                  clear-icon="cancel"
                  class="text-left mb-5"
                  :error-messages="otpErrorMessages"
                  @update:model-value="checkOTPErrors"
                  @focus="checkOTPErrors"
                >
                  <template v-slot:append-inner>
                    <div v-if="!invalidOtp" style="display: inline-block" aria-live="assertive">
                      <span style="position: absolute; left: -2000px">Input is valid</span>
                      <v-icon color="success" :icon="mdiCheckCircle" />
                    </div>
                  </template>
                </OnexPasscode>
                <v-row class="font-size--12 text-left mt-3">
                  <v-col cols="12" v-if="!hideResend">
                    <v-row>
                      <v-col cols="12" class="pb-0 pt-2 body-2 font-size--14">
                        {{ $t('L_didnt_receive_one') }}
                        <a href="javascript:void(0)" @click="resendOTP" class="nounderline">{{
                          sending ? $t('L_sending') : $t('L_resend_code')
                        }}</a>
                      </v-col>
                      <v-col cols="12" class="pt-0 body-2 font-size--14">
                        {{ $t('L_please_wait_25_seconds') }}
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" v-if="trustDeviceOption">
                    {{ $t('L_for_your_added_security') }}
                  </v-col>
                  <div class="text-center pr-0 pl-0 pt-4 mt-4" style="width: 100%">
                    <OnexButton
                      v-if="!maxedOut"
                      color="primary"
                      class="mb-3 mx-2 btnMaxWidth"
                      text="Primary Medium"
                      :disabled="invalidOtp"
                      @click="submitAction"
                    >
                      {{ $t('L_next') }}
                    </OnexButton>
                  </div>
                  <div class="text-center pr-0 pl-0" style="width: 100%">
                    <OnexButton
                      color="primary"
                      variant="outlined"
                      class="mb-3 mx-2 btnMaxWidth"
                      text="Primary Medium"
                      :loading="loading"
                      :disabled="false"
                      @click="closeAction"
                    >
                      {{ $t('L_close') }}
                    </OnexButton>
                  </div>
                </v-row>
              </v-col>
            </v-row>
          </form>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { minLength, required } from '@vuelidate/validators';
import { phoneNumber as phoneNumberFormatter } from '@/shared/util/formatters';
import AlertsComponent from '../AlertsComponent.vue';
import { CHANNELS } from '@/shared/models/constants.models';
import { useVuelidate } from '@vuelidate/core';
import { mapActions, mapState } from 'pinia';
import { useHomeStore } from '@/modules/home/store/home.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { mdiCheckCircle } from '@mdi/js';
import { OnexButton, OnexPasscode } from '@primerica/onex-ui-lib';
import { RouteNames } from '@/router/routeNames';

const STEPS = {
  MAIN_ACTION: 0,
  CHANNELS: 1,
  SUBMITOTP: 2,
};

const MAX_RESEND_COUNT = 3;
const MAX_ATTEMPT_COUNT = 3;

const componentOptions: ComponentOptions = {
  name: 'TwoFactorWrapper',
  components: { AlertsComponent, OnexButton, OnexPasscode },
  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    alertContext: {
      type: String,
      required: true,
      default: 'verify_phone',
    },
    twoFactorPayload: {
      type: Object,
      required: true,
      default: () => ({ channel: CHANNELS.SMS, phone: '', email: '' }),
    },
    submitPayload: {
      type: Object,
      required: true,
      default: () => {},
    },
    trustDeviceOption: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    currentStep: STEPS.MAIN_ACTION,
    otpCode: '',
    otpErrorMessages: [],
    attemptCount: 0,
    resendCount: 0,
    otpCodeExpired: false,
    maxedOut: false,
    submitActionPayload: {},
    sending: false,
    v$: useVuelidate(),
    mdiCheckCircle,
    RouteNames,
    isResend: false,
  }),
  validations() {
    return {
      otpCode: {
        required,
        minLength: minLength(6),
      },
    };
  },
  computed: {
    ...mapState(useHomeStore, {
      loading: 'loading',
      token: 'mfaToken',
    }),
    invalidOtp() {
      return this.v$.otpCode.$invalid || this.otpCode.length < 1;
    },
    hideResend() {
      return this.resendCount === MAX_RESEND_COUNT && this.maxedOut;
    },
  },
  methods: {
    ...mapActions(useHomeStore, {
      createChallenge: 'createChallenge',
      setLoading: 'setLoading',
      setMfaToken: 'setMfaToken',
    }),
    ...mapActions(useGlobalStore, {
      addError: 'addError',
      addAlert: 'addAlert',
      removeAlerts: 'removeAlerts',
      updateMobileNumber: 'updateMobileNumber',
    }),
    goBackToEdit() {
      this.otpCode = '';
      this.otpErrorMessages = [];
      this.attemptCount = 0;
      this.resendCount = 0;
      this.maxedOut = false;
      this.$emit('gotoupdatecontact');
    },
    requestOTP(bypassNum: string) {
      this.removeAlerts(this.alertContext);
      this.setLoading(true);
      if (bypassNum && bypassNum.length > 0) {
        this.twoFactorPayload.phone = bypassNum;
      }
      const challengeSpec = {
        viewContext: this.alertContext,
        payload: this.twoFactorPayload,
      };
      // && this.token !== ''
      if (this.isResend) {
        challengeSpec.payload.token = this.token;
      }
      this.createChallenge(challengeSpec)
        .then(this.handleOTPSentSuccess)
        .catch((error: unknown) => {
          this.handleOTPSentFailure(error);
        })
        .finally(() => this.setLoading(false));
    },
    resendOTP() {
      if (this.sending) return;
      this.maxedOut = false;
      this.otpErrorMessages = [];
      this.sending = true;
      this.attemptCount = 0;
      this.resendCount += 1;
      setTimeout(() => {
        this.sending = false;
      }, 25000);
      this.isResend = true;
      this.requestOTP();
    },
    handleOTPSentSuccess() {
      this.currentStep = STEPS.SUBMITOTP;
      this.addAlert({
        type: 'success',
        details: {
          message: this.$t('L_verification_code_sent_sms_or_email', {
            type: this.$t(`L_sent_${this.twoFactorPayload.channel}`),
          }),
        },
        context: this.alertContext,
      });
    },
    handleOTPSentFailure() {
      this.currentStep = STEPS.SUBMITOTP;
    },
    submitAction() {
      this.setLoading(true);
      this.otpErrorMessages = [];
      const submitActionPayload = {
        ...this.submitPayload,
        token: this.token,
        verified: true,
        otp: this.otpCode,
      };
      this.updateMobileNumber(submitActionPayload)
        .then(() => {
          this.$emit('success');
          this.otpCode = '';
          this.otpErrorMessages = [];
          this.attemptCount = 0;
          this.resendCount = 0;
          this.maxedOut = false;
        })
        .catch(this.handleSubmitErrors)
        .finally(() => {
          this.setLoading(false);
        });
    },
    closeAction() {
      // this.$refs.twoFactorForm.reset();
      // this.currentStep = STEPS.MAIN_ACTION;
      // this.v$.reset();
      this.setMfaToken('');
      this.otpCode = '';
      this.otpErrorMessages = [];
      this.attemptCount = 0;
      this.resendCount = 0;
      this.otpCodeExpired = false;
      this.maxedOut = false;
      this.submitActionPayload = {};
      this.sending = false;
      this.$emit('closeverifyphonedialog');
    },
    handleSubmitErrors(error: {
      response: { data: { statusCode: number; appStatusCode: string } };
    }) {
      const status = error.response.data.statusCode;
      if (!status || status !== 400) {
        this.$emit('error', error);
        return;
      }
      this.handleOTPErrors(error);
    },
    handleOTPErrors(error: { response: { data: { statusCode: number; appStatusCode: string } } }) {
      const errorCode = error.response.data.appStatusCode;
      let otpErrorMessage = '';

      if (errorCode === 'dcp-3003' || errorCode === 'dcp-3004') {
        this.attemptCount += 1;
        if (
          this.resendCount < MAX_RESEND_COUNT ||
          (this.resendCount === MAX_RESEND_COUNT && this.attemptCount < MAX_ATTEMPT_COUNT)
        ) {
          switch (this.attemptCount) {
            case 1:
              otpErrorMessage = this.$t('L_otp_code_first_attempt');
              break;
            case 2:
              otpErrorMessage = this.$t('L_otp_code_second_attempt');
              break;
            case 3:
              otpErrorMessage = this.$t('L_otp_code_exceeded');
              this.maxedOut = true;
              break;
            default:
              otpErrorMessage = this.$t('L_generic_invalid');
          }
        } else if (
          this.resendCount > MAX_RESEND_COUNT ||
          (this.resendCount === MAX_RESEND_COUNT && this.attemptCount === MAX_ATTEMPT_COUNT)
        ) {
          otpErrorMessage = this.$t('L_otp_enter_correct_no');
          this.maxedOut = true;
        }
        this.otpErrorMessages.push(otpErrorMessage);
      }

      if (errorCode === 'dcp-3001') {
        this.maxedOut = true;
        if (this.resendCount < MAX_RESEND_COUNT) {
          this.otpErrorMessages.push(this.$t('L_otp_code_exceeded'));
        } else if (this.resendCount >= MAX_RESEND_COUNT) {
          this.otpErrorMessages.push(this.$t('L_otp_enter_correct_no'));
        }
      }

      if (errorCode === 'dcp-3002') {
        this.otpCodeExpired = true;
        this.otpErrorMessages.push(this.$t('L_otp_code_expired'));
      }
    },
    checkOTPErrors() {
      const validationError = [];
      if (!this.v$.otpCode.required) {
        validationError.push(this.$t('L_otp_required_field'));
      }
      if (!this.v$.otpCode.minLength) {
        validationError.push(this.$t('L_otp_code_min_length'));
      }
      this.otpErrorMessages = validationError;
    },
    formattedContactNumber(number: string) {
      if (number) {
        return phoneNumberFormatter(number);
      }
      return '-';
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style>
.btnMaxWidth {
  max-width: 250px !important;
  width: 350px;
}
.editItHere {
  color: #0c6dca;
  font-weight: bold;
  cursor: pointer;
}
.fixed-top-alert {
  position: absolute;
  top: -4px;
  z-index: 1;
  width: 100%;
}
.v-btn.close-otp {
  position: absolute;
  top: 0;
  right: 0;
}
.nounderline {
  text-decoration: none;
}
</style>
